import {
  BRAND_LOGO,
  ErrorState,
  LoadingState,
  Logo,
  SkipToContent,
  Surface,
} from '@beautyburst/ui';
import { semanticColor, space } from '@beautyburst/tokens';
import { useEffect, useMemo, useState } from 'react';
import { App } from './App.tsx';
import type { AdminApiClient, AdminBootstrap } from './api-client.ts';
import { createAdminHttpClient, AdminApiError } from './http-client.ts';
import { resolveAdminRoute } from './routes.ts';
import { AdminScreen, AdminSignInScreen } from './screens.tsx';
import { adminAsset, adminHref } from './base-path.ts';
import { MfaStepUpDialog, useMfaStepUpChallenge } from './mfa.tsx';

type BootstrapState =
  | { readonly status: 'loading' }
  | { readonly status: 'loaded'; readonly value: AdminBootstrap }
  | { readonly status: 'failed'; readonly error: unknown };

function AdministratorSignIn({
  client,
  onSignedIn,
}: {
  client: AdminApiClient;
  onSignedIn: () => void;
}) {
  return (
    <Surface surface="admin">
      <SkipToContent />
      <header
        style={{
          minHeight: '64px',
          display: 'flex',
          alignItems: 'center',
          padding: `0 ${space[4]}`,
          background: semanticColor.surfaceCard,
          borderBottom: `1px solid ${semanticColor.borderDefault}`,
        }}
      >
        <Logo src={adminAsset(BRAND_LOGO.full)} variant="full" />
      </header>
      <AdminSignInScreen client={client} onSignedIn={onSignedIn} />
    </Surface>
  );
}

export function BrowserAdminApp() {
  const challenge = useMfaStepUpChallenge();
  const client = useMemo(
    () => createAdminHttpClient({ onMfaRequired: challenge.requestStepUp }),
    [challenge.requestStepUp],
  );
  const [path, setPath] = useState(() => window.location.pathname);
  const [state, setState] = useState<BootstrapState>({ status: 'loading' });
  const [version, setVersion] = useState(0);
  const route = resolveAdminRoute(path);

  useEffect(() => {
    if (route?.permission === null) return;
    let active = true;
    setState({ status: 'loading' });
    void client.bootstrap.get().then(
      (value) => active && setState({ status: 'loaded', value }),
      (error: unknown) => active && setState({ status: 'failed', error }),
    );
    return () => {
      active = false;
    };
  }, [client, route?.permission, version]);

  useEffect(() => {
    const popstate = () => setPath(window.location.pathname);
    const click = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey
      )
        return;
      const element = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (
        !(element instanceof HTMLAnchorElement) ||
        element.target !== '' ||
        element.hasAttribute('download')
      )
        return;
      const destination = new URL(element.href, window.location.origin);
      if (destination.origin !== window.location.origin) return;
      event.preventDefault();
      window.history.pushState(
        {},
        '',
        `${destination.pathname}${destination.search}${destination.hash}`,
      );
      setPath(destination.pathname);
    };
    window.addEventListener('popstate', popstate);
    document.addEventListener('click', click);
    return () => {
      window.removeEventListener('popstate', popstate);
      document.removeEventListener('click', click);
    };
  }, []);

  const showSignIn =
    route?.permission === null ||
    (state.status === 'failed' &&
      state.error instanceof AdminApiError &&
      state.error.code === 'UNAUTHENTICATED');

  const mfaDialog = (
    <MfaStepUpDialog
      client={client.mfa}
      open={challenge.open}
      onClose={challenge.close}
      onComplete={() => {
        challenge.complete();
        setVersion((value) => value + 1);
      }}
    />
  );

  if (showSignIn) {
    return (
      <AdministratorSignIn
        client={client}
        onSignedIn={() => {
          window.history.replaceState({}, '', adminHref('/'));
          setPath(adminHref('/'));
          setVersion((value) => value + 1);
        }}
      />
    );
  }
  if (state.status === 'loading')
    return (
      <>
        <LoadingState label="Loading administrator console" />
        {mfaDialog}
      </>
    );
  if (state.status === 'failed') {
    const mfa = state.error instanceof AdminApiError && state.error.code === 'PRECONDITION_FAILED';
    return (
      <>
        <ErrorState
          headingLevel={1}
          title={mfa ? 'Multi-factor authentication required' : 'Administrator console unavailable'}
          description={
            mfa
              ? 'Complete MFA for this session, then try again.'
              : 'We could not verify the administrator session. Try again shortly.'
          }
          retry={
            <button type="button" onClick={() => setVersion((value) => value + 1)}>
              Retry session check
            </button>
          }
        />
        {mfaDialog}
      </>
    );
  }
  return (
    <>
      <App
        path={path}
        actor={state.value.actor}
        flags={state.value.flags}
        renderScreen={(route) => (
          <AdminScreen
            route={route}
            client={client}
            flags={state.value.flags}
            onRequestStepUp={challenge.requestStepUp}
          />
        )}
      />
      {mfaDialog}
    </>
  );
}
